export interface EnvVarPair {
  key: string;
  value: string;
}

export function parseEnvText(text: string): { envVars: EnvVarPair[]; errors: string[] } {
  const lines = text.split('\n');
  const envVars: EnvVarPair[] = [];
  const errors: string[] = [];
  const keysSeen = new Set<string>();

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith('#')) {
      continue;
    }

    const eqIdx = line.indexOf('=');
    if (eqIdx === -1) {
      errors.push(`Line ${i + 1} is invalid: missing "=" (format must be KEY=value)`);
      continue;
    }

    const key = line.substring(0, eqIdx).trim();
    const rawValue = line.substring(eqIdx + 1).trim();

    if (!key) {
      errors.push(`Line ${i + 1} is invalid: key cannot be empty`);
      continue;
    }

    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(key)) {
      errors.push(`Line ${i + 1} is invalid: key "${key}" contains invalid characters (must start with letter/underscore, contain only alphanumeric/underscores)`);
      continue;
    }

    if (keysSeen.has(key)) {
      errors.push(`Line ${i + 1} is invalid: duplicate key "${key}"`);
      continue;
    }
    keysSeen.add(key);

    let parsedVal: string;
    if (rawValue.length >= 2 && (rawValue.startsWith('"') || rawValue.startsWith("'"))) {
      const quoteChar = rawValue[0];
      let unescapedValue = "";
      let j = 1;
      let closed = false;
      while (j < rawValue.length) {
        const char = rawValue[j];
        if (char === '\\') {
          if (j + 1 < rawValue.length) {
            const nextChar = rawValue[j + 1];
            if (quoteChar === '"') {
              if (nextChar === 'n') unescapedValue += '\n';
              else if (nextChar === 'r') unescapedValue += '\r';
              else if (nextChar === 't') unescapedValue += '\t';
              else unescapedValue += nextChar;
            } else { // single quote
              if (nextChar === '\'' || nextChar === '\\') unescapedValue += nextChar;
              else unescapedValue += '\\' + nextChar;
            }
            j += 2;
          } else {
            unescapedValue += '\\';
            j++;
          }
        } else if (char === quoteChar) {
          closed = true;
          j++;
          break;
        } else {
          unescapedValue += char;
          j++;
        }
      }

      if (closed) {
        const remainder = rawValue.substring(j).trim();
        if (remainder && !remainder.startsWith('#')) {
          errors.push(`Line ${i + 1} is invalid: invalid characters after close quote`);
          continue;
        }
        parsedVal = unescapedValue;
      } else {
        errors.push(`Line ${i + 1} is invalid: unclosed quote`);
        continue;
      }
    } else {
      // Unquoted value
      let unquotedVal = rawValue;
      const commentMatch = unquotedVal.match(/(?:\s|^)#.*/);
      if (commentMatch) {
        unquotedVal = unquotedVal.substring(0, commentMatch.index);
      }
      parsedVal = unquotedVal.trim();
    }

    envVars.push({ key, value: parsedVal });
  }

  return { envVars, errors };
}

export function formatEnvText(envVars: EnvVarPair[]): string {
  return envVars.map(v => {
    const val = v.value || "";
    const needsQuoting = /[\s'"#]/.test(val);
    if (needsQuoting) {
      const escaped = val
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t');
      return `${v.key}="${escaped}"`;
    }
    return `${v.key}=${val}`;
  }).join('\n');
}
